import React, { useContext, useCallback, useState, useEffect } from "react";
import Table from "components/Table/Table";
import { InstallationModeContext } from "contexts/InstallationMode/installation-mode-context";
import { Tag, Tooltip, Modal } from "antd";
import { Order_By } from "pacts/app-webcore/hasura-webcore.graphql";
import { formatFromNow } from "utils/date";
import { capitalize, get, uniq } from "lodash";
import { Link } from "react-router-dom";
import { LineChartOutlined } from "@ant-design/icons";
import useSensorflowLocation from "hooks/use-sensorflow-location";
import ActionButton from "components/ActionButton";
import ModalWrapper from "components/Modal/Wrapper";
import useModalWrapperTrigger from "hooks/use-modal-wrapper-trigger";
import { Infrastructure } from "../Infrastructures.d";
import { InfrastructureTableProps } from "./InfrastructureTable.d";
import { InfrastructureType } from "../InfrastructureDataType";
import PowerVsLoadModal from "../PowerVsLoadModal/PowerVsLoadModal";
import DeleteInfrastructureModal from "../InfrastructureBlock/DeleteInfrastructureModal/DeleteInfrastructureModal";

const sortOrder = (value: Order_By) => get({ [Order_By.Asc]: "ascend", [Order_By.Desc]: "descend" }, value, "");

const getColorTag = (status?: string) => {
  const defaultColor = "default";
  const colorTags = {
    online: "success",
    offline: "error",
  };

  return colorTags[status as keyof typeof colorTags] || defaultColor;
};

const formatInfrastructureType = (type: string): string => {
  if (!type) return "";

  return type
    .split("_")
    .map((word) => capitalize(word.toLowerCase()))
    .join(" ");
};

const InfrastructureTable: React.FC<InfrastructureTableProps> = ({
  data,
  pagination,
  sort = {},
  sortBy = () => {},
  onPaginationChange = () => {},
  rowSelection,
  locationId,
  locationName,
  isCustomer = false,
}) => {
  const { currentPositionId } = useContext(InstallationModeContext);
  const { location } = useSensorflowLocation();
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedInfra, setSelectedInfra] = useState<any>();
  const [deleteInfrastructureModalRef, openDeleteInfrastructureModal] = useModalWrapperTrigger();
  const [infrastructureToDelete, setInfrastructureToDelete] = useState<Infrastructure | null>(null);
  const [shouldOpenDeleteModal, setShouldOpenDeleteModal] = useState(false);

  // Effect to open delete modal after infrastructureToDelete is set
  useEffect(() => {
    if (infrastructureToDelete && shouldOpenDeleteModal) {
      try {
        openDeleteInfrastructureModal();
        setShouldOpenDeleteModal(false);
      } catch (error) {
        Modal.error({
          title: "Error",
          content: "An error occurred while opening the delete modal. Please try again.",
        });
        setShouldOpenDeleteModal(false);
      }
    }
  }, [infrastructureToDelete, shouldOpenDeleteModal, openDeleteInfrastructureModal]);

  const openChartModal = (record: Infrastructure) => {
    setSelectedInfra(record);
    setChartModalOpen(true);
  };

  const handleChartIconClick = (e: React.MouseEvent, record: Infrastructure) => {
    e.stopPropagation();
    openChartModal(record);
  };

  const onDeleteButtonClick = (record: Infrastructure) => {
    try {
      const connectedNodes = record?.meterPosition?.nodes || [];
      if (connectedNodes.length > 0) {
        Modal.warning({
          title: "Warning",
          content:
            "Deleting a compressor with mapped nodes is not allowed, please unmap all nodes from compressors before deleting.",
        });
      } else {
        setInfrastructureToDelete(record);
        setShouldOpenDeleteModal(true);
      }
    } catch (error) {
      Modal.error({
        title: "Error",
        content: "An error occurred while trying to delete the infrastructure. Please try again.",
      });
    }
  };

  const handleDeleteSuccess = () => {
    if (infrastructureToDelete) {
      setInfrastructureToDelete(null);
      setShouldOpenDeleteModal(false);
    }
  };

  const getSortConfig = (sortKey: string) => {
    return {
      onHeaderCell: () => ({
        onClick: () => sortBy(sortKey),
      }),
      sorter: true,
      sortOrder: sortOrder(sort[sortKey]),
    };
  };

  const columns = [
    {
      title: "Infrastructure",
      render: (record: Infrastructure) =>
        isCustomer ? (
          <span>{record?.name}</span>
        ) : (
          <Link
            to={{
              pathname: `/locations/${locationId}/infrastructures/${record.id}`,
              state: {
                locationName,
              },
            }}
          >
            <span>{record?.name}</span>
          </Link>
        ),
      visibleOnDetailView: false,
      ...getSortConfig("name"),
    },
    {
      title: "Keys Connected",
      render: (record: Infrastructure) => {
        const infraType = new InfrastructureType(record?.type as string);
        if (!infraType.isCompressor()) return "";
        const aggregatedKeys =
          record?.rooms?.length > 0
            ? uniq(record?.rooms.map((r) => r.position?.parentPosition?.positionName)).join(", ")
            : "None";

        return <span>{aggregatedKeys}</span>;
      },
      visibleOnDetailView: false,
    },
    {
      title: "Type",
      render: (record: Infrastructure) => {
        const infraType = record?.type || "";
        return <span>{formatInfrastructureType(infraType)}</span>;
      },
      visibleOnDetailView: false,
    },
    {
      title: "Power Vs Load",
      render: (record: Infrastructure) => {
        const infraType = record?.type?.toLowerCase() || "";
        const showPowerVsLoad = infraType.includes("compressor") || infraType.includes("chiller");

        return showPowerVsLoad ? (
          <Tooltip title="Power Vs Load">
            <LineChartOutlined
              className="text-primary"
              aria-hidden="true"
              onClick={(e) => handleChartIconClick(e, record)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              aria-label="View Power Vs Load"
            />
          </Tooltip>
        ) : (
          <span>N/A</span>
        );
      },
      visibleOnDetailView: false,
      align: "center",
    },
    {
      title: "Status",
      render: (record: Infrastructure) => {
        const connectedNodes = record?.meterPosition?.nodes || [];
        const nodeConnected = connectedNodes[0];
        const status = (
          currentPositionId === record?.id ? nodeConnected?.nodeStatusInInstallationMode : nodeConnected?.status
        ) as string;
        const colorTag = getColorTag(status || "");

        return <Tag color={colorTag}>{capitalize(status || "Not mapped")}</Tag>;
      },
      visibleOnDetailView: false,
    },
    ...(isCustomer
      ? []
      : [
          {
            title: "Compressor Location",
            render: (record: Infrastructure) => {
              const positionType = record.position?.positionType as string;
              const validTypes = new Set(["floor", "group", "key", "room", "zone"]);
              const locName = validTypes.has(positionType) ? record.position?.positionName : "--";
              return <span>{locName}</span>;
            },
            visibleOnDetailView: false,
          },
        ]),
    ...(isCustomer
      ? []
      : [
          {
            title: "Node Connected",
            render: (record: Infrastructure) => {
              const infraType = new InfrastructureType(record?.type as string);
              const connectedNodes = record?.meterPosition?.nodes || [];
              const nodeConnected = connectedNodes[0];
              if (!nodeConnected || infraType.isCoolingSystem()) return "--";

              return <span>{nodeConnected.nodeMacId}</span>;
            },
            visibleOnDetailView: false,
          },
          {
            title: "Last Seen",
            render: (record: Infrastructure) => {
              const infraType = new InfrastructureType(record?.type as string);
              const connectedNodes = record?.meterPosition?.nodes || [];
              const nodeConnected = connectedNodes[0];
              if (!nodeConnected || infraType.isCoolingSystem()) return "";

              return <span>{formatFromNow(nodeConnected.lastSeen)}</span>;
            },
            visibleOnDetailView: false,
          },
        ]),
    ...(isCustomer
      ? []
      : [
          {
            title: "Actions",
            width: 100,
            render: (record: Infrastructure) => (
              <ActionButton
                type="delete"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDeleteButtonClick(record);
                }}
                className="fs-xl"
              />
            ),
            visibleOnDetailView: false,
            align: "center",
          },
        ]),
  ];

  const genRowClassNames = useCallback(() => (isCustomer ? {} : { "cursor-pointer": true }), [isCustomer]);

  return (
    <>
      <Table
        columns={columns}
        rowKey="id"
        tableData={data || []}
        rowClassName={genRowClassNames}
        pagination={{ ...pagination, pageSizeOption: ["10", "20", "50", "100"], showSizeChanger: true }}
        handleTableChange={onPaginationChange}
        rowSelection={rowSelection}
      />
      {selectedInfra && (
        <PowerVsLoadModal
          isOpen={chartModalOpen}
          onClose={() => setChartModalOpen(false)}
          infrastructureName={selectedInfra.name}
          locationName={location?.locationName || ""}
          locationId={location?.id || ""}
          infrastructureType={selectedInfra.type}
        />
      )}
      {infrastructureToDelete && (
        <ModalWrapper
          ref={deleteInfrastructureModalRef}
          modal={DeleteInfrastructureModal}
          props={{
            infraId: infrastructureToDelete.id,
            name: infrastructureToDelete.name || "",
            onSuccess: handleDeleteSuccess,
          }}
        />
      )}
    </>
  );
};

export default InfrastructureTable;
